import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BarChart3,
    Users,
    Settings,
    Target,
    BookOpen,
    MessageSquare,
    LogOut,
    Headphones,
    Presentation,
    Grid,
    Package,
    Zap,
    CheckSquare,
    BarChart3 as BarChartIcon,
    Shield,
    Brain,
    TestTube,
    Calendar,
    Activity,
    FileText,
    DollarSign,
    TrendingUp,
    Heart,
    ChevronUp,
    ChevronDown,
} from 'lucide-react';

// Core components (always loaded)
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { SENSAIProvider } from '../../sensai/SENSAIProvider';
import { SENSAILauncher } from '../../sensai/SENSAILauncher';
import { ControlBridgeThemeToggle } from './ThemeToggle';
import { useTheme } from '../../lib/theme-context.js';

// Lazy loading components
import { LazyLoader, DashboardSkeleton, PanelSkeleton } from '../common/LazyLoader';
import { useCache } from '../../hooks/useCache';

// Lazy loaded modules with caching
const UnifiedDashboard = lazy(() =>
    import('./UnifiedDashboard').then(m => ({ default: m.UnifiedDashboard }))
);
const ProjectWorkspace = lazy(() =>
    import('./ProjectWorkspace').then(m => ({ default: m.ProjectWorkspace }))
);
const CommunicationCenter = lazy(() =>
    import('./CommunicationCenter').then(m => ({ default: m.CommunicationCenter }))
);
const OperationsControl = lazy(() =>
    import('./OperationsControl').then(m => ({ default: m.OperationsControl }))
);
const KnowledgeFiles = lazy(() =>
    import('./KnowledgeFiles').then(m => ({ default: m.KnowledgeFiles }))
);
const Administration = lazy(() =>
    import('./Administration').then(m => ({ default: m.Administration }))
);
const SupportBridge = lazy(() =>
    import('./SupportBridge').then(m => ({ default: m.SupportBridge }))
);
const PresentationStudio = lazy(() => import('./PresentationStudio'));
const KnowledgeIntelligencePanel = lazy(() =>
    import('./Knowledge/KnowledgeIntelligencePanel').then(m => ({
        default: m.KnowledgeIntelligencePanel,
    }))
);
const SmartGridComponent = lazy(() => import('./SmartGrid/SmartGridComponent'));
const AutomationWorkflowBuilder = lazy(() => import('./SmartGrid/AutomationWorkflowBuilder'));
const TodoTemplates = lazy(() => import('./Templates').then(m => ({ default: m.TodoTemplates })));
const AirtableWidgets = lazy(() => import('./Widgets').then(m => ({ default: m.AirtableWidgets })));
const AgenticMultiViewManager = lazy(() =>
    import('./Agentic').then(m => ({ default: m.AgenticMultiViewManager }))
);
const SmartWorkflowAutomation = lazy(() =>
    import('./Agentic').then(m => ({ default: m.SmartWorkflowAutomation }))
);
const AdaptiveUXOrchestrator = lazy(() =>
    import('./Agentic').then(m => ({ default: m.AdaptiveUXOrchestrator }))
);
const TriageModule = lazy(() =>
    import('./Triage/TriageModule').then(m => ({ default: m.TriageModule }))
);
const SecurityScannerPanel = lazy(() =>
    import('./SecurityScanner/SecurityScannerPanel').then(m => ({
        default: m.SecurityScannerPanel,
    }))
);
const ProjectDashboard = lazy(() =>
    import('./ProjectManagement/ProjectDashboard').then(m => ({ default: m.ProjectDashboard }))
);
const PMODashboard = lazy(() =>
    import('./PMO/PMODashboard').then(m => ({ default: m.PMODashboard }))
);
const RuntimeEfficiencyDashboard = lazy(() =>
    import('./RuntimeEfficiencyOps/RuntimeEfficiencyDashboard').then(m => ({
        default: m.RuntimeEfficiencyDashboard,
    }))
);
const IntegrationHub = lazy(() =>
    import('./Integration/IntegrationHub').then(m => ({ default: m.IntegrationHub }))
);
const ImplementationCockpit = lazy(() =>
    import('./Cockpit/ImplementationCockpit').then(m => ({ default: m.ImplementationCockpit }))
);
const ImplementationLabPanel = lazy(() =>
    import('./ImplementationLabPanel').then(m => ({ default: m.ImplementationLabPanel }))
);
const PrecisionDataPanel = lazy(() => import('./PrecisionData/PrecisionDataPanel'));
const CodeOpsWorkspace = lazy(() => import('./CodeOps/CodeOpsWorkspace'));
const ConversionDashboard = lazy(() => import('../../panels/conversion/ConversionDashboard'));
const QADashboard = lazy(() => import('./QA/QADashboard').then(m => ({ default: m.QADashboard })));
const StoryboardKnowledgeBase = lazy(() =>
    import('./KnowledgeBase/StoryboardKnowledgeBase').then(m => ({
        default: m.StoryboardKnowledgeBase,
    }))
);
const MeetingScribePage = lazy(() =>
    import('./MeetingScribe/MeetingScribePage').then(m => ({ default: m.MeetingScribePage }))
);
const AnnotationSystem = lazy(() =>
    import('./Annotation/AnnotationSystem').then(m => ({ default: m.AnnotationSystem }))
);
const CommunicationHub = lazy(() =>
    import('./CommunicationHub/CommunicationHub').then(m => ({ default: m.CommunicationHub }))
);
const PMSenseiPage = lazy(() =>
    import('./PMSensei/PMSenseiPage').then(m => ({ default: m.PMSenseiPage }))
);
const OnboardingHub = lazy(() => import('./OnboardingHub/OnboardingHub'));
const AnalyticsModule = lazy(() => import('./Analytics/AnalyticsModule'));
const AnalyticsPanel = lazy(() => import('../Analytics/AnalyticsPanel'));
const UnifiedAnalyticsDashboard = lazy(() => import('./Analytics/UnifiedAnalyticsDashboard'));
const EnhancedSmartGrid = lazy(() => import('../SmartGrid/EnhancedSmartGrid'));
const SwarmHealthPanel = lazy(() =>
    import('./SwarmHealth/SwarmHealthPanel').then(m => ({ default: m.SwarmHealthPanel }))
);
const BriefingPanel = lazy(() =>
    import('./Briefing/BriefingPanel').then(m => ({ default: m.BriefingPanel }))
);
const ChatAidPanel = lazy(() =>
    import('./ChatAid/ChatAidPanel').then(m => ({ default: m.ChatAidPanel }))
);
const CommandCenterDashboard = lazy(() => import('./CommandCenter/CommandCenterDashboard'));
const IntegratedCommandCenter = lazy(() =>
    import('../CommandCenter/IntegratedCommandCenter').then(module => ({
        default: module.IntegratedCommandCenter,
    }))
);
const AppDockPanel = lazy(() =>
    import('./AppDock/AppDockPanel').then(m => ({ default: m.AppDockPanel }))
);
const BuildLabIntelligenceDashboard = lazy(() =>
    import('./BuildLab/BuildLabIntelligenceDashboard').then(m => ({ default: m.default }))
);

// Always load these critical components
import { CodeOpsIcon } from './CodeOps/components/CodeOpsIcon';
import { SvgDefsRoot } from './Widgets/SvgDefsRoot';

interface ControlBridgePanelProps {
    className?: string;
}

type ActiveModule =
    | 'work-objects'
    | 'unified-dashboard'
    | 'project-workspace'
    | 'communication-center'
    | 'operations-control'
    | 'knowledge-files'
    | 'administration'
    | 'support-bridge'
    | 'code-ops'
    | 'conversion-ops'
    | 'qa-dashboard'
    | 'presentation-studio'
    | 'smart-grid'
    | 'automation'
    | 'todo-templates'
    | 'airtable-widgets'
    | 'agentic-multiview'
    | 'smart-workflow'
    | 'adaptive-ux'
    | 'triage'
    | 'security-scanner'
    | 'overview'
    | 'projects'
    | 'pmo'
    | 'runtime-efficiency'
    | 'onboarding'
    | 'meetingscribe'
    | 'integrations'
    | 'cockpit'
    | 'knowledge'
    | 'knowledge-intelligence'
    | 'briefing'
    | 'chataid'
    | 'communication'
    | 'annotation'
    | 'pmsensai'
    | 'analytics'
    | 'swarm-health'
    | 'implementation-lab'
    | 'precision-data'
    | 'command-center'
    | 'app-dock'
    | 'buildlab-intelligence';

interface DashboardMetric {
    id: string;
    title: string;
    value: string;
    change: string;
    trend: 'up' | 'down';
    icon: any;
    color: string;
}

export const ControlBridgePanel: React.FC<ControlBridgePanelProps> = ({ className = '' }) => {
    const { user, logout, getVisibleModules, isAuthenticated } = useAuth();
    const { theme } = useTheme();
    const [activeModule, setActiveModule] = useState<ActiveModule>('unified-dashboard');
    const [showAuth, setShowAuth] = useState(!isAuthenticated);
    const [metrics, setMetrics] = useState<DashboardMetric[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

    const defaultUser = {
        id: 'default-user',
        name: 'Guest User',
        email: 'guest@example.com',
        roles: ['viewer'],
        orgType: 'internal' as const,
        orgId: 'default-org',
        token: '',
    };

    useEffect(() => {
        const currentTheme = theme || 'classic';
        document.documentElement.setAttribute(
            'data-theme',
            currentTheme === 'classic' ? 'dark' : currentTheme
        );
        localStorage.setItem('control-bridge-theme', currentTheme);
    }, [theme]);

    const getModulesForUser = () => {
        const currentUser = user || defaultUser;
        if (!currentUser) return [];

        const userRole = currentUser?.roles?.[0] || 'admin';
        const visibleModules = getVisibleModules();

        const roleModules = {
            projectManager: [
                { id: 'work-objects', label: 'Work Objects', icon: Target, group: 'overview' },
                { id: 'unified-dashboard', label: 'Dashboard', icon: BarChart3, group: 'overview' },
                { id: 'app-dock', label: 'APP DOCK', icon: Package, group: 'overview' },
                { id: 'project-workspace', label: 'Projects', icon: Target, group: 'management' },
                { id: 'smart-workflow', label: 'Workflows', icon: Zap, group: 'management' },
                {
                    id: 'todo-templates',
                    label: 'Templates',
                    icon: CheckSquare,
                    group: 'management',
                },
                {
                    id: 'presentation-studio',
                    label: 'Presentations',
                    icon: Presentation,
                    group: 'content',
                },
                { id: 'meetingscribe', label: 'Meeting Scribe', icon: Calendar, group: 'content' },
                { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'insights' },
                { id: 'smart-grid', label: 'Smart Grid', icon: Grid, group: 'insights' },
                { id: 'airtable-widgets', label: 'Widgets', icon: BarChartIcon, group: 'insights' },
                { id: 'administration', label: 'Admin', icon: Users, group: 'settings' },
                {
                    id: 'communication',
                    label: 'Communication',
                    icon: MessageSquare,
                    group: 'settings',
                },
                {
                    id: 'support-bridge',
                    label: 'Support Bridge',
                    icon: Headphones,
                    group: 'support',
                },
                { id: 'code-ops', label: 'CodeOps', icon: Brain, group: 'system' },
                { id: 'conversion-ops', label: 'ConversionOps', icon: TrendingUp, group: 'system' },
                { id: 'qa-dashboard', label: 'QA Dashboard', icon: TestTube, group: 'system' },
                { id: 'swarm-health', label: 'Health Analysis', icon: Heart, group: 'system' },
            ],
            businessAnalyst: [
                { id: 'work-objects', label: 'Work Objects', icon: Target, group: 'overview' },
                { id: 'unified-dashboard', label: 'Dashboard', icon: BarChart3, group: 'overview' },
                { id: 'app-dock', label: 'APP DOCK', icon: Package, group: 'overview' },
                { id: 'knowledge-files', label: 'Knowledge Base', icon: BookOpen, group: 'data' },
                { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'data' },
                { id: 'smart-grid', label: 'Smart Grid', icon: Grid, group: 'data' },
                { id: 'airtable-widgets', label: 'Widgets', icon: BarChartIcon, group: 'data' },
                {
                    id: 'presentation-studio',
                    label: 'Presentations',
                    icon: Presentation,
                    group: 'content',
                },
                { id: 'annotation', label: 'Annotations', icon: FileText, group: 'content' },
                { id: 'agentic-multiview', label: 'Multi-View', icon: Target, group: 'insights' },
                { id: 'meetingscribe', label: 'Meeting Scribe', icon: Calendar, group: 'tools' },
                { id: 'todo-templates', label: 'Templates', icon: CheckSquare, group: 'tools' },
                {
                    id: 'support-bridge',
                    label: 'Support Bridge',
                    icon: Headphones,
                    group: 'support',
                },
                { id: 'qa-dashboard', label: 'QA Dashboard', icon: TestTube, group: 'system' },
                { id: 'swarm-health', label: 'Health Analysis', icon: Heart, group: 'system' },
            ],
            systemArchitect: [
                { id: 'work-objects', label: 'Work Objects', icon: Target, group: 'overview' },
                { id: 'unified-dashboard', label: 'Dashboard', icon: BarChart3, group: 'overview' },
                { id: 'app-dock', label: 'APP DOCK', icon: Package, group: 'overview' },
                { id: 'operations-control', label: 'Operations', icon: Settings, group: 'system' },
                { id: 'code-ops', label: 'CodeOps', icon: Brain, group: 'system' },
                { id: 'conversion-ops', label: 'ConversionOps', icon: TrendingUp, group: 'system' },
                { id: 'qa-dashboard', label: 'QA Dashboard', icon: TestTube, group: 'system' },
                {
                    id: 'buildlab-intelligence',
                    label: 'BuildLab Intelligence',
                    icon: Brain,
                    group: 'system',
                },
                { id: 'triage', label: 'System Triage', icon: Shield, group: 'system' },
                {
                    id: 'security-scanner',
                    label: 'Security Scanner',
                    icon: Activity,
                    group: 'system',
                },
                { id: 'automation', label: 'Automation', icon: Zap, group: 'development' },
                { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'development' },
                { id: 'smart-grid', label: 'Smart Grid', icon: Grid, group: 'development' },
                { id: 'precision-data', label: 'Precision Data', icon: Activity, group: 'tools' },
                { id: 'swarm-health', label: 'Health Analysis', icon: Heart, group: 'tools' },
                { id: 'meetingscribe', label: 'Meeting Scribe', icon: Calendar, group: 'content' },
                { id: 'annotation', label: 'Annotation', icon: FileText, group: 'content' },
                {
                    id: 'support-bridge',
                    label: 'Support Bridge',
                    icon: Headphones,
                    group: 'support',
                },
            ],
            client: [
                { id: 'unified-dashboard', label: 'Dashboard', icon: BarChart3, group: 'overview' },
                { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'overview' },
                {
                    id: 'presentation-studio',
                    label: 'Presentations',
                    icon: Presentation,
                    group: 'content',
                },
                { id: 'knowledge-files', label: 'Documents', icon: BookOpen, group: 'content' },
                { id: 'support-bridge', label: 'Support', icon: Headphones, group: 'support' },
                {
                    id: 'communication',
                    label: 'Communication',
                    icon: MessageSquare,
                    group: 'support',
                },
                { id: 'meetingscribe', label: 'Meeting Notes', icon: Calendar, group: 'tools' },
            ],
        };

        const userModules = roleModules[userRole as keyof typeof roleModules] || [
            { id: 'unified-dashboard', label: 'Dashboard', icon: BarChart3, group: 'overview' },
            { id: 'app-dock', label: 'APP DOCK', icon: Package, group: 'overview' },
            {
                id: 'project-workspace',
                label: 'Project Workspace',
                icon: Target,
                group: 'management',
            },
            {
                id: 'communication-center',
                label: 'Communication Center',
                icon: MessageSquare,
                group: 'communication',
            },
            {
                id: 'operations-control',
                label: 'Operations Control',
                icon: Settings,
                group: 'system',
            },
            { id: 'knowledge-files', label: 'Knowledge & Files', icon: BookOpen, group: 'data' },
            { id: 'administration', label: 'Administration', icon: Users, group: 'settings' },
            { id: 'support-bridge', label: 'Support Bridge', icon: Headphones, group: 'support' },
            { id: 'code-ops', label: 'CodeOps', icon: Brain, group: 'development' },
            {
                id: 'conversion-ops',
                label: 'ConversionOps',
                icon: TrendingUp,
                group: 'development',
            },
            { id: 'qa-dashboard', label: 'QA Dashboard', icon: TestTube, group: 'system' },
            {
                id: 'presentation-studio',
                label: 'Presentation Studio',
                icon: Presentation,
                group: 'content',
            },
            { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'development' },
            { id: 'smart-grid', label: 'Smart Grid', icon: Grid, group: 'development' },
            { id: 'precision-data', label: 'Precision Data', icon: Activity, group: 'tools' },
            { id: 'swarm-health', label: 'Health Analysis', icon: Heart, group: 'tools' },
            { id: 'meetingscribe', label: 'Meeting Scribe', icon: Calendar, group: 'content' },
            { id: 'annotation', label: 'Annotation', icon: FileText, group: 'content' },
            { id: 'triage', label: 'System Triage', icon: Shield, group: 'system' },
            { id: 'automation', label: 'Automation', icon: Zap, group: 'development' },
            { id: 'airtable-widgets', label: 'Widgets', icon: BarChartIcon, group: 'insights' },
            { id: 'todo-templates', label: 'Templates', icon: CheckSquare, group: 'management' },
        ];

        if (!user) {
            return userModules;
        }

        return userModules.filter(module => visibleModules.includes(module.id));
    };

    const modules = getModulesForUser();

    const fetchDashboardMetrics = async () => {
        try {
            setIsLoading(true);

            const mockMetrics: DashboardMetric[] = [
                {
                    id: 'active-projects',
                    title: 'Active Projects',
                    value: '24',
                    change: '+12%',
                    trend: 'up',
                    icon: Target,
                    color: 'text-blue-400',
                },
                {
                    id: 'budget-utilization',
                    title: 'Budget Utilization',
                    value: '78%',
                    change: '+5%',
                    trend: 'up',
                    icon: DollarSign,
                    color: 'text-green-400',
                },
                {
                    id: 'team-velocity',
                    title: 'Team Velocity',
                    value: '42 pts',
                    change: '+8%',
                    trend: 'up',
                    icon: TrendingUp,
                    color: 'text-[#5A7BC4]',
                },
                {
                    id: 'integration-health',
                    title: 'Integration Health',
                    value: '96%',
                    change: '-2%',
                    trend: 'down',
                    icon: Zap,
                    color: 'text-orange-400',
                },
            ];

            setMetrics(mockMetrics);
        } catch (error) {
            console.error('Failed to fetch dashboard metrics:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const renderActiveModule = () => {
        const renderComponent = () => {
            switch (activeModule) {
                case 'unified-dashboard':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <UnifiedDashboard />
                        </Suspense>
                    );
                case 'project-workspace':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <ProjectWorkspace />
                        </Suspense>
                    );
                case 'communication-center':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <CommunicationCenter />
                        </Suspense>
                    );
                case 'operations-control':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <OperationsControl />
                        </Suspense>
                    );
                case 'knowledge-files':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <KnowledgeFiles />
                        </Suspense>
                    );
                case 'administration':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <Administration />
                        </Suspense>
                    );
                case 'support-bridge':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <SupportBridge />
                        </Suspense>
                    );
                case 'code-ops':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <CodeOpsWorkspace />
                        </Suspense>
                    );
                case 'conversion-ops':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <ConversionDashboard />
                        </Suspense>
                    );
                case 'qa-dashboard':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <QADashboard repo="DDPMLS/beehive-core" branch="Main" />
                        </Suspense>
                    );
                case 'presentation-studio':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <PresentationStudio />
                        </Suspense>
                    );
                case 'smart-grid':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <EnhancedSmartGrid
                                entity="control-bridge-smartgrid"
                                workbookName="Control Bridge Workbook"
                                enableAI={true}
                                enableCollaboration={true}
                            />
                        </Suspense>
                    );
                case 'projects':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <ProjectDashboard />
                        </Suspense>
                    );
                case 'analytics':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <UnifiedAnalyticsDashboard />
                        </Suspense>
                    );
                case 'swarm-health':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <SwarmHealthPanel />
                        </Suspense>
                    );
                case 'pmo':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <PMODashboard />
                        </Suspense>
                    );
                case 'runtime-efficiency':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <RuntimeEfficiencyDashboard />
                        </Suspense>
                    );
                case 'onboarding':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <OnboardingHub />
                        </Suspense>
                    );
                case 'pmsensai':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <PMSenseiPage />
                        </Suspense>
                    );
                case 'communication':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <CommunicationHub />
                        </Suspense>
                    );
                case 'meetingscribe':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <MeetingScribePage />
                        </Suspense>
                    );
                case 'integrations':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <IntegrationHub />
                        </Suspense>
                    );
                case 'automation':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <AutomationWorkflowBuilder
                                workflows={[]}
                                onWorkflowCreate={(workflow: any) =>
                                    console.log('Creating workflow:', workflow)
                                }
                                onWorkflowUpdate={(id: string, updates: any) =>
                                    console.log('Updating workflow:', id, updates)
                                }
                                onWorkflowDelete={(id: string) =>
                                    console.log('Deleting workflow:', id)
                                }
                            />
                        </Suspense>
                    );
                case 'todo-templates':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <TodoTemplates />
                        </Suspense>
                    );
                case 'airtable-widgets':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <AirtableWidgets />
                        </Suspense>
                    );
                case 'agentic-multiview':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <AgenticMultiViewManager />
                        </Suspense>
                    );
                case 'smart-workflow':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <SmartWorkflowAutomation />
                        </Suspense>
                    );
                case 'adaptive-ux':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <AdaptiveUXOrchestrator />
                        </Suspense>
                    );
                case 'triage':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <TriageModule />
                        </Suspense>
                    );
                case 'security-scanner':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <SecurityScannerPanel />
                        </Suspense>
                    );
                case 'cockpit':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <ImplementationCockpit />
                        </Suspense>
                    );
                case 'knowledge':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <StoryboardKnowledgeBase />
                        </Suspense>
                    );
                case 'knowledge-intelligence':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <KnowledgeIntelligencePanel />
                        </Suspense>
                    );
                case 'briefing':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <BriefingPanel />
                        </Suspense>
                    );
                case 'chataid':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <ChatAidPanel />
                        </Suspense>
                    );
                case 'annotation':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <AnnotationSystem />
                        </Suspense>
                    );
                case 'implementation-lab':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <ImplementationLabPanel />
                        </Suspense>
                    );
                case 'precision-data':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <PrecisionDataPanel />
                        </Suspense>
                    );
                case 'command-center':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <IntegratedCommandCenter
                                tenantId={(user || defaultUser)?.orgId || 'default'}
                                userRole={(user || defaultUser)?.roles?.[0] || 'viewer'}
                                userPermissions={[]}
                            />
                        </Suspense>
                    );
                case 'app-dock':
                    return (
                        <Suspense fallback={<PanelSkeleton />}>
                            <AppDockPanel />
                        </Suspense>
                    );
                case 'buildlab-intelligence':
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <BuildLabIntelligenceDashboard />
                        </Suspense>
                    );
                default:
                    return (
                        <Suspense fallback={<DashboardSkeleton />}>
                            <UnifiedDashboard />
                        </Suspense>
                    );
            }
        };

        return (
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeModule}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="w-full h-full"
                >
                    {renderComponent()}
                </motion.div>
            </AnimatePresence>
        );
    };

    // if (!isAuthenticated || showAuth) {
    //   return (
    //     <CinematicAuth
    //       onAuthSuccess={() => setShowAuth(false)}
    //       backgroundComponent={
    //         <div className={`h-full flex flex-col ${className}`} style={{ background: 'var(--control-bridge-backdrop-solid)' }}>
    //           <div className="geex-card rounded-2xl p-8 mb-6 shadow-3xl border border-slate-700/50">
    //             <div className="flex items-center justify-between mb-8">
    //               <div className="flex items-center space-x-4">
    //                 <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl p-1">
    //                   <img
    //                     src="/modular-system-grid-stripe.png"
    //                     alt="Modular System Grid Stripe Logo"
    //                     className="w-full h-full object-contain rounded-xl"
    //                     onError={(e) => console.error('Logo failed to load:', e)}
    //                   />
    //                 </div>
    //                 <div>
    //                   <motion.h1
    //                     className="text-4xl font-bold bg-gradient-to-r from-slate-100 to-slate-200 bg-clip-text text-transparent text-shadow mb-3"
    //                     animate={{ x: [0, 3, 0] }}
    //                     transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    //                   >
    //                     Control Bridge
    //                   </motion.h1>
    //                   <p className="text-slate-300 text-lg font-medium">Comprehensive Implementation Hub</p>
    //                 </div>
    //               </div>
    //               <div className="flex items-center space-x-4">
    //                 <div className="status-indicator-enhanced"></div>
    //                 <span className="text-sm font-medium" style={{ color: 'var(--cb-text-secondary)' }}>
    //                   All Systems Operational
    //                 </span>
    //               </div>
    //             </div>
    //             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
    //               {[1, 2, 3, 4].map((i) => (
    //                 <div key={i} className="geex-card rounded-2xl p-6 border border-slate-700/50">
    //                   <div className="h-20 bg-slate-700/50 rounded animate-pulse" />
    //                 </div>
    //               ))}
    //             </div>
    //           </div>
    //         </div>
    //       }
    //     />
    //   );
    // }

    const mapUserRoleToSENSAIRole = (
        userRole: string
    ): 'viewer' | 'operator' | 'supervisor' | 'admin' => {
        switch (userRole) {
            case 'admin':
            case 'super_admin':
                return 'admin';
            case 'projectManager':
            case 'manager':
                return 'supervisor';
            case 'businessAnalyst':
            case 'systemArchitect':
            case 'user':
                return 'operator';
            case 'client':
            case 'viewer':
            default:
                return 'viewer';
        }
    };

    const sensaiRole = mapUserRoleToSENSAIRole((user || defaultUser)?.roles?.[0] || 'viewer');

    return (
        <SENSAIProvider userRole={sensaiRole}>
            <SvgDefsRoot />
            <div
                className={`control-bridge-container h-full flex flex-col ${className}`}
                style={{ background: theme === 'light' ? 'var(--bg)' : '#181a1f' }}
            >
                <div
                    className={
                        theme === 'light'
                            ? 'geex-card border border-slate-700/50 p-8'
                            : 'geex-card p-8'
                    }
                >
                    <AnimatePresence>
                        {!isHeaderCollapsed && (
                            <motion.div
                                className="flex items-center justify-between mb-8"
                                initial={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                            >
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-3">
                                        {theme === 'light' ? (
                                            <div className="w-16 h-16 rounded-2xl geex-card flex items-center justify-center shadow-2xl p-2">
                                                <img
                                                    src="/tyler-technologies-logo.png"
                                                    alt="Tyler Technologies Logo"
                                                    className="w-full h-full object-contain"
                                                    onError={e =>
                                                        console.error(
                                                            'Tyler Technologies logo failed to load:',
                                                            e
                                                        )
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <img
                                                src="/tyler-technologies-logo.png"
                                                alt="Tyler Technologies Logo"
                                                className="w-16 h-16 object-contain rounded-xl"
                                                onError={e =>
                                                    console.error('Logo failed to load:', e)
                                                }
                                            />
                                        )}
                                        <div>
                                            <motion.h1
                                                className={
                                                    theme === 'light'
                                                        ? 'text-4xl font-bold bg-gradient-to-r from-[#4267A1] to-[#8A9150] bg-clip-text text-transparent text-shadow'
                                                        : 'text-4xl font-bold bg-gradient-to-r from-slate-100 to-slate-200 bg-clip-text text-transparent text-shadow'
                                                }
                                                animate={{ x: [0, 3, 0] }}
                                                transition={{
                                                    duration: 8,
                                                    repeat: Infinity,
                                                    ease: 'easeInOut',
                                                }}
                                            >
                                                Control Bridge
                                            </motion.h1>
                                            <p
                                                style={{ color: 'var(--text-muted)' }}
                                                className="text-lg font-medium"
                                            >
                                                Comprehensive Implementation Hub
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div
                                        className="flex items-center gap-2 text-sm"
                                        style={{ color: 'var(--text-muted)' }}
                                    >
                                        <span>Welcome, {(user || defaultUser)?.name}</span>
                                        <Badge
                                            variant="secondary"
                                            className={
                                                theme === 'light'
                                                    ? 'bg-gray-200 text-gray-700'
                                                    : 'bg-slate-700 text-slate-300'
                                            }
                                        >
                                            {(user || defaultUser)?.orgType}
                                        </Badge>
                                    </div>
                                    <ControlBridgeThemeToggle />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={logout}
                                        className={
                                            theme === 'light'
                                                ? 'text-slate-400 hover:text-white'
                                                : 'text-slate-400 hover:text-white'
                                        }
                                    >
                                        <LogOut className="w-4 h-4" />
                                    </Button>
                                    <div className="status-indicator-enhanced"></div>
                                    <span
                                        className="text-sm font-medium"
                                        style={{ color: 'var(--text-muted)' }}
                                    >
                                        All Systems Operational
                                    </span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="cb-nav-tabs flex items-center justify-between gap-1 sm:gap-2 overflow-x-auto pb-3 scrollbar-hide">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
                            className="text-slate-400 flex-shrink-0 mr-2 hover:bg-transparent hover:text-slate-400"
                        >
                            {isHeaderCollapsed ? (
                                <ChevronDown className="w-4 h-4" />
                            ) : (
                                <ChevronUp className="w-4 h-4" />
                            )}
                        </Button>
                        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-hide">
                            {modules.map(module => {
                                const Icon = module.icon;
                                return (
                                    <motion.button
                                        key={module.id}
                                        onClick={() => setActiveModule(module.id as ActiveModule)}
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`cb-nav-tab flex items-center justify-center px-2 py-3 sm:px-4 rounded-xl transition-all duration-300 font-medium min-w-[44px] min-h-[44px] whitespace-nowrap relative group ${
                                            activeModule === module.id
                                                ? theme === 'light'
                                                    ? 'bg-[#4267A1] text-white shadow-lg shadow-[#4267A1]/20 border border-[#4267A1]/30'
                                                    : 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] border border-[#4267A1]/30 text-white shadow-lg shadow-[#4267A1]/20'
                                                : theme === 'light'
                                                  ? 'bg-gray-200 border border-gray-300 text-gray-700 hover:bg-gray-300 hover:border-gray-400'
                                                  : 'geex-card border border-slate-700/50 hover:border-[#4267A1]/50 hover:bg-slate-800/80 text-slate-300 hover:text-white hover:shadow-md hover:shadow-[#4267A1]/10'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                        <span className="hidden group-hover:inline ml-2 text-xs sm:text-sm">
                                            {module.label}
                                        </span>

                                        <div
                                            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-12 px-3 py-2 geex-card border border-slate-600 text-white text-xs rounded-lg opacity-0 hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap z-[9999] sm:hidden shadow-xl group-hover:opacity-100"
                                            aria-hidden="true"
                                        >
                                            <span className="sr-only">{module.label}</span>
                                            {module.label}
                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-slate-800"></div>
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeModule}
                            initial={{ opacity: 0, y: 30, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -30, scale: 0.98 }}
                            transition={{ duration: 0.4, ease: 'easeInOut' }}
                            className="min-h-full"
                        >
                            {renderActiveModule()}
                        </motion.div>
                    </AnimatePresence>
                </div>
                <SENSAILauncher variant="fixed" />
            </div>
        </SENSAIProvider>
    );
};

export default ControlBridgePanel;
